import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import dayjs from 'dayjs'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Control, Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import type { CardfileData } from '@/api/cardfile/cardfileApi'
import { dictionariesQueries } from '@/api/dictionaries/dictionariesQueries'
import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomDatePicker from '@/components/Atoms/AtomDatePicker/AtomDatePicker'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { getLegalFieldsFromCardfile } from '@/components/Cardfile/lib/cardfileToLegalFields'
import VatLookup from '@/components/Cardfile/VatLookup/VatLookup'
import { DELAY, PAYMENT_TYPES } from '@/constants'
import { usePasswordVisibility } from '@/hooks/ui/usePasswordVisibility'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputClearErrorsType,
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  SelectFormControlType,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { toBackendDateTime } from '@/utils/api/toBackendDateTime'
import { generatePassword } from '@/utils/auth/generatePassword'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import Checkboxes from '../../Checkboxes/Checkboxes'
import {
  GENERAL_FIELDS,
  LEGAL_FIELDS,
  legalCreationSchema,
  type LegalCreationType,
  type PhysicalCreationType,
} from '../Forms.config'
import cn from '../Forms.module.scss'
import {
  pickSharedValues,
  type RegisterFormHandle,
  SHARED_FIELD_NAMES,
} from '../lib/sharedFields'

const LegalForm = forwardRef<RegisterFormHandle>(function LegalForm(_, ref) {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    getFieldState,
    clearErrors,
    setError,
    setFocus,
    watch,
    trigger,
    formState: { errors, isValid, dirtyFields },
  } = useForm<LegalCreationType>({
    shouldFocusError: true,
    resolver: zodResolver(legalCreationSchema),
    mode: 'onChange',
  })

  useImperativeHandle(
    ref,
    () => ({
      getSharedValues: () => pickSharedValues(getValues()),
      setSharedValues: (values) => {
        const sync = { shouldDirty: true }
        if (values.surname !== undefined)
          setValue('surname', values.surname, sync)
        if (values.name !== undefined) setValue('name', values.name, sync)
        if (values.patronymic !== undefined)
          setValue('patronymic', values.patronymic, sync)
        if (values.phoneNumber !== undefined)
          setValue('phoneNumber', values.phoneNumber, sync)
        if (values.email !== undefined) setValue('email', values.email, sync)
        if (values.password !== undefined)
          setValue('password', values.password, sync)
        if (values.repeatPassword !== undefined)
          setValue('repeatPassword', values.repeatPassword, sync)
        if (values.paymentType !== undefined)
          setValue('paymentType', values.paymentType, sync)
        if (values.privacyPolicyAgreement !== undefined)
          setValue(
            'privacyPolicyAgreement',
            values.privacyPolicyAgreement,
            sync
          )
        const invalidFields = SHARED_FIELD_NAMES.filter(
          (name) => getFieldState(name).error
        )
        if (invalidFields.length > 0) trigger(invalidFields)
      },
    }),
    [getValues, setValue, getFieldState, trigger]
  )

  const verifiedVatRef = useRef<string | null>(null)

  const parentOrganisationId = watch('parentOrganisationId')
  const vatNumber = watch('vatNumber') || ''
  const password = watch('password')
  useEffect(() => {
    if (dirtyFields.repeatPassword) {
      trigger('repeatPassword')
    }
  }, [password])

  const [isSubmitting, setSubmitting] = useState(false)
  const paymentTypesQuery = useQuery(dictionariesQueries.paymentTypes())
  const currenciesQuery = useQuery(dictionariesQueries.currencies())

  const [paymentSelectOpen, setPaymentSelectOpen] = useState(false)
  const [currencySelectOpen, setCurrencySelectOpen] = useState(false)

  const { userId } = useUserInfoStore((state) => state)

  const [modal, contextHolder] = Modal.useModal()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleVatVerified = (parentId?: string) => {
    verifiedVatRef.current = vatNumber
    setValue('parentOrganisationId', parentId)
    clearErrors('vatNumber')
    if (parentId) toast.success('Отмечено как филиал')
  }

  const handleVatDuplicate = (message: string) => {
    verifiedVatRef.current = null
    setError('vatNumber', { type: 'manual', message })
  }

  const handleCardfileApply = (data: CardfileData) => {
    getLegalFieldsFromCardfile(data).forEach(([name, value]) =>
      setValue(name, value, { shouldValidate: true, shouldDirty: true })
    )
    toast.success('Данные из картотеки подставлены')
  }

  const onSubmit = (data: LegalCreationType) => {
    if (verifiedVatRef.current !== data.vatNumber) {
      setError('vatNumber', {
        type: 'manual',
        message: 'Запросите данные из картотеки, чтобы проверить УНП',
      })
      setFocus('vatNumber')
      toast.error('Проверьте УНП')
      return
    }

    const searchParamsToken = searchParams.get('token')
    setSubmitting(true)

    const backendPhoneNumber = denormalizePhoneForBackend(data.phoneNumber)
    // organisation.ipRegistrationDate is a `date-time` field — a bare "YYYY-MM-DD"
    // fails backend JSON parsing ("Failed to read request"). Send a full timestamp.
    const ipRegistrationDate = toBackendDateTime(data.ipRegistrationDate)

    const surname = data.surname.trim()
    const name = data.name.trim()
    const patronymic = data.patronymic?.trim() || ''

    const fullDescription = [surname, name, patronymic]
      .filter(Boolean)
      .join(' ')

    const loadingToast = toast.loading('Регистрация организации...', {
      description: data.shortLegalName,
    })

    const createRegisterRequest = (bankId: string | null) => {
      const requestBody = {
        organisation: {
          description: data.description,
          vatNumber: data.vatNumber,
          fullLegalName: data.fullLegalName,
          shortLegalName: data.shortLegalName,
          legalAddress: data.legalAddress,
          postalAddress: data.postalAddress,
          phoneNumber: backendPhoneNumber,
          // Backend requires organisation.email in the body; the form field is optional,
          // so send an empty string instead of letting the key drop out of the request.
          email: data.email || '',
          contactPhone: backendPhoneNumber,
          ...(ipRegistrationDate && { ipRegistrationDate }),
          ...(data.parentOrganisationId && {
            parentOrganisationId: data.parentOrganisationId,
          }),
          // supervisorManager is an optional uuid — only send it when we actually have a
          // logged-in user id, never as an empty string (backend rejects "" as a uuid).
          ...(!searchParamsToken && userId && { supervisorManager: userId }),
        },
        user: {
          description: fullDescription,
          name: name,
          phoneNumber: backendPhoneNumber,
          // Backend requires user.email in the body; keep the key present even when blank.
          email: data.email || '',
          login: backendPhoneNumber,
          password: data.password,
          roles: ['ADMIN'],
        },
        contractPaymentType: data.paymentType.value,
        bankAccounts: bankId
          ? [
              {
                iban: data.iban,
                currency: data.currency.value,
                bank: bankId,
                isMain: true,
              },
            ]
          : [],
        deliveryAddresses: [],
        contactInfos: [],
      }

      return api.post('/organisations/register', requestBody, {
        params: {
          ...(searchParamsToken && { token: searchParamsToken }),
        },
        _skipSanitize: true,
      })
    }

    const ibanPromise = data.iban
      ? api.get('/banks/by-iban', { params: { iban: data.iban } })
      : Promise.resolve({ data: { id: null } })

    ibanPromise
      .then((response) => {
        const bankId = response.data.bank?.id || response.data.id
        if (!bankId && data.iban) {
          throw new Error('BANK_NOT_FOUND')
        }
        return createRegisterRequest(bankId)
      })
      .then(() => {
        setTimeout(() => {
          toast.success('Организация зарегистрирована', {
            id: loadingToast,
            description: `${data.shortLegalName} — ${data.vatNumber}`,
          })
          setSubmitting(false)
          navigate('/register/success')
        }, DELAY * 3)
      })
      .catch((e) => {
        toast.dismiss(loadingToast)
        setSubmitting(false)

        if (e.message === 'BANK_NOT_FOUND') {
          toast.error('Банк не найден по IBAN', {
            description: 'Проверьте правильность ввода IBAN',
          })
          return
        }
        handleApiError(e, modal)
      })
  }

  const { showPasswords, getVisibilityProps } = usePasswordVisibility()

  const generatePasswordСaller = () => {
    generatePassword(setValue, clearErrors as FormInputClearErrorsType)
    showPasswords()
  }

  return (
    <>
      {contextHolder}
      <form
        className={cn.form}
        onSubmit={handleSubmit(onSubmit, () => handleFormSubmitError(isValid))}
        autoComplete="off"
      >
        <div className={cn.inputsBlock}>
          <div className={cn.vatWrapper}>
            <AtomFormInput
              location="register"
              idPrefix="legal-"
              layout="stacked"
              name={'vatNumber' as FormInputNameType}
              title="УНП"
              errors={errors}
              register={register as FormInputRegisterType}
              setValue={setValue}
              control={control as FormInputControlType}
              readOnly={false}
              autoComplete="off"
              preventAutofill={true}
              setValueAs={(value: string) =>
                value.replace(/\D/g, '').slice(0, 9)
              }
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 9)
                setValue('vatNumber', value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
                if (errors.vatNumber?.type === 'manual')
                  clearErrors('vatNumber')
                if (getValues('parentOrganisationId'))
                  setValue('parentOrganisationId', undefined)
              }}
            />
            <VatLookup
              key={vatNumber}
              vatNumber={vatNumber}
              isBranch={Boolean(parentOrganisationId)}
              onVerified={handleVatVerified}
              onDuplicate={handleVatDuplicate}
              onApply={handleCardfileApply}
            />
          </div>
          <p className={cn.legalInformationTitle}>
            Информация о вашем юридическом лице
          </p>
          {LEGAL_FIELDS.map((field) => {
            const { name, title, required } = field

            if (name === 'ipRegistrationDate') {
              return (
                <div key={field.id} className={cn.dateField}>
                  <label htmlFor="ipRegistrationDate">{title}</label>
                  <Controller
                    control={control}
                    name="ipRegistrationDate"
                    render={({ field: dateField }) => {
                      const parsed = dateField.value
                        ? dayjs(dateField.value)
                        : null
                      return (
                        <AtomDatePicker
                          id="ipRegistrationDate"
                          className={cn.datePicker}
                          placeholder="дд.мм.гггг"
                          inputReadOnly
                          value={parsed && parsed.isValid() ? parsed : null}
                          onChange={(date) =>
                            dateField.onChange(
                              date ? date.format('YYYY-MM-DD') : ''
                            )
                          }
                        />
                      )
                    }}
                  />
                </div>
              )
            }

            return (
              <AtomFormInput
                key={field.id}
                location="register"
                idPrefix="legal-"
                layout="stacked"
                name={name as FormInputNameType}
                title={title}
                required={required}
                errors={errors}
                register={register as FormInputRegisterType}
                control={control as FormInputControlType}
                setValue={setValue}
                readOnly={false}
                preventAutofill={true}
                autoComplete="off"
                setValueAs={
                  name === 'iban'
                    ? (value: string) => value.replace(/\s/g, '')
                    : undefined
                }
              />
            )
          })}
          <p className={cn.generalInformationTitle}>Общая информация</p>
          {GENERAL_FIELDS.map((field) => {
            const { name, required, title } = field

            const isPassword = name === 'password' || name === 'repeatPassword'
            const shouldBlockAutofill =
              name === 'email' || name === 'name' || name === 'surname'

            return (
              <AtomFormInput
                key={field.id}
                location="register"
                idPrefix="legal-"
                layout="stacked"
                title={title}
                register={register as FormInputRegisterType}
                name={name as FormInputNameType}
                required={required}
                autoComplete={isPassword ? 'new-password' : 'off'}
                preventAutofill={shouldBlockAutofill}
                errors={name === 'email' || required ? errors : undefined}
                setValue={setValue}
                control={control as FormInputControlType}
                readOnly={false}
                generatePasswordCaller={
                  name === 'password' ? generatePasswordСaller : undefined
                }
                {...getVisibilityProps(name)}
              />
            )
          })}
          <div className={cn.selectsGroup}>
            <HoverElement>
              <AtomSelect
                title="Способ оплаты"
                options={paymentTypesQuery.data?.map((paymentType) => ({
                  value: paymentType.id,
                  label: PAYMENT_TYPES[paymentType.paymentType],
                }))}
                required
                formControl={control as SelectFormControlType}
                layout="stacked"
                formErrors={errors}
                formName="paymentType"
                loading={paymentTypesQuery.isLoading}
                open={paymentSelectOpen}
                openByHover={false}
                onOpenChange={setPaymentSelectOpen}
                onChange={() => setPaymentSelectOpen(false)}
              />
            </HoverElement>
            <HoverElement>
              <AtomSelect
                title="Валюта"
                options={currenciesQuery.data?.map((currency) => ({
                  value: currency.id,
                  label: currency.description,
                }))}
                required
                formControl={control as SelectFormControlType}
                layout="stacked"
                formErrors={errors}
                formName="currency"
                loading={currenciesQuery.isLoading}
                open={currencySelectOpen}
                openByHover={false}
                onOpenChange={setCurrencySelectOpen}
                onChange={() => setCurrencySelectOpen(false)}
              />
            </HoverElement>
          </div>
        </div>
        <Checkboxes
          control={control as Control<PhysicalCreationType | LegalCreationType>}
          errors={errors}
        />
        <div className={cn.submitButtonContainer}>
          <AtomButton
            fullWidth
            htmlType="submit"
            loading={isSubmitting}
            onClick={() => {
              if (
                (!getValues('privacyPolicyAgreement') ||
                  !dirtyFields.privacyPolicyAgreement) &&
                Object.keys(dirtyFields).length === GENERAL_FIELDS.length
              ) {
                toast.error('Требуется согласие', {
                  description:
                    'Подтвердите согласие с Политикой конфиденциальности',
                })
                return
              }
              if (!isValid) {
                toast.warning('Проверьте форму', {
                  description: 'Заполните все обязательные поля корректно',
                })
              }
            }}
          >
            Зарегистрировать
          </AtomButton>
        </div>
      </form>
    </>
  )
})

export default LegalForm
